import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { 
  CreditCard, 
  Users, 
  DollarSign, 
  RefreshCw, 
  Search, 
  Filter, 
  AlertCircle, 
  CheckCircle,
  Clock,
  XCircle,
  TrendingUp,
  Calendar,
  Eye,
  Database,
  Loader2
} from 'lucide-react';
import AdminNavigation from '@/components/AdminNavigation';
import { LoadingState } from '@/components/LoadingState';
import { useToast } from '@/hooks/use-toast';

interface StripeTransaction {
  id: string;
  amount: number;
  currency: string;
  status: string;
  description?: string;
  customer?: string;
  created: number;
  payment_method_types?: string[]; // Optional for backward compatibility
  automatic_payment_methods?: {
    enabled: boolean;
  };
  charges?: {
    data: Array<{
      billing_details?: {
        email?: string;
      };
    }>;
  };
}

interface StripeCustomer {
  id: string;
  email?: string;
  name?: string;
  created: number;
  metadata?: {
    [key: string]: string;
  };
  subscription_status?: string;
  total_spent?: number;
  payment_methods?: string;
}

interface StripeSubscription {
  id: string;
  customer: string;
  status: string;
  current_period_start: number;
  current_period_end: number;
  items: {
    data: Array<{
      price: {
        nickname?: string;
        unit_amount: number;
        currency: string;
      };
    }>;
  };
}

interface StripeApiResponse<T> {
  data: T[];
  has_more: boolean;
  total_count: number;
}

interface StripeStats {
  total_revenue: number;
  total_transactions: number;
  active_subscriptions: number;
  total_customers: number;
  period: string;
}

interface StripeSyncStatus {
  total_users: number;
  users_with_stripe: number;
  users_without_stripe: number;
  lifetime_users: number;
  sync_percentage: number;
  lifetime_product_exists: boolean;
}

interface StripeSyncResult {
  success: boolean;
  message: string;
  results?: {
    total_processed: number;
    success: string[];
    failed: string[];
    lifetime_customers: string[];
  };
  summary?: {
    total_users_processed: number;
    success_count: number;
    failed_count: number;
    lifetime_count: number;
    sync_percentage_after: number;
  };
}

export default function AdminPayments() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('30d');
  const [currentPage, setCurrentPage] = useState(1);

  // Build query URLs with proper parameter serialization
  const buildStatsUrl = () => `/api/admin/stripe/stats/${dateRange}`;
  const buildTransactionsUrl = () => {
    const params: string[] = ['limit=50'];
    if (statusFilter !== 'all') params.push(`status=${encodeURIComponent(statusFilter)}`);
    return `/api/admin/stripe/transactions?${params.join('&')}`;
  };
  const buildCustomersUrl = () => `/api/admin/stripe/customers?limit=50`;
  const buildSubscriptionsUrl = () => {
    const params: string[] = ['limit=50'];
    if (statusFilter !== 'all') params.push(`status=${encodeURIComponent(statusFilter)}`);
    return `/api/admin/stripe/subscriptions?${params.join('&')}`;
  };

  // Get Stripe statistics
  const { data: stripeStats, isLoading: statsLoading } = useQuery({
    queryKey: ['/api/admin/stripe/stats', dateRange],
    queryFn: () => fetch(buildStatsUrl()).then(res => res.json()),
    select: (response) => response as StripeStats,
  });

  // Get Stripe transactions (payment intents)
  const { data: transactionsData, isLoading: transactionsLoading, refetch: refetchTransactions } = useQuery({
    queryKey: ['/api/admin/stripe/transactions', statusFilter],
    queryFn: () => fetch(buildTransactionsUrl()).then(res => res.json()),
    select: (response) => response as StripeApiResponse<StripeTransaction>,
  });

  // Get Stripe customers
  const { data: customersData, isLoading: customersLoading, refetch: refetchCustomers } = useQuery({
    queryKey: ['/api/admin/stripe/customers'],
    queryFn: () => fetch(buildCustomersUrl()).then(res => res.json()),
    select: (response) => response as StripeApiResponse<StripeCustomer>,
  });

  // Get Stripe subscriptions
  const { data: subscriptionsData, isLoading: subscriptionsLoading, refetch: refetchSubscriptions } = useQuery({
    queryKey: ['/api/admin/stripe/subscriptions', statusFilter],
    queryFn: () => fetch(buildSubscriptionsUrl()).then(res => res.json()),
    select: (response) => response as StripeApiResponse<StripeSubscription>,
  });

  const transactions = transactionsData?.data || [];
  const customers = customersData?.data || [];
  const subscriptions = subscriptionsData?.data || [];

  // Get Stripe sync status
  const { data: syncStatus, isLoading: syncStatusLoading, refetch: refetchSyncStatus } = useQuery({
    queryKey: ['/api/admin/stripe/sync-status'],
    queryFn: () => fetch('/api/admin/stripe/sync-status').then(res => res.json()),
    select: (response) => response.data as StripeSyncStatus,
  });

  // Sync customers mutation
  const syncCustomersMutation = useMutation({
    mutationFn: () => fetch('/api/admin/stripe/sync-customers', { method: 'POST' }).then(res => res.json()),
    onSuccess: (data: StripeSyncResult) => {
      toast({
        title: "Sync Complete",
        description: data.message,
        variant: data.success ? "default" : "destructive",
      });
      
      // Refetch all data after successful sync
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['/api/admin/stripe'] });
        refetchSyncStatus();
        refetchCustomers();
        refetchTransactions();
        refetchSubscriptions();
      }
    },
    onError: (error: any) => {
      toast({
        title: "Sync Failed",
        description: error.message || "Failed to sync customers with Stripe",
        variant: "destructive",
      });
    }
  });

  // Helper function to get customer email from transaction
  const getCustomerEmail = (transaction: StripeTransaction) => {
    // Try to get email from billing details in charges
    if (transaction.charges?.data?.[0]?.billing_details?.email) {
      return transaction.charges.data[0].billing_details.email;
    }
    return 'N/A';
  };

  // Filter functions
  const filteredTransactions = transactions.filter(transaction => {
    const customerEmail = getCustomerEmail(transaction);
    const matchesSearch = !searchTerm || 
      customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || transaction.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredCustomers = customers.filter(customer => 
    !searchTerm || 
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Utility functions
  const formatCurrency = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'succeeded':
      case 'active':
        return 'default';
      case 'pending':
      case 'incomplete':
        return 'secondary';
      case 'failed':
      case 'canceled':
        return 'destructive';
      case 'requires_action':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'succeeded':
      case 'active':
        return <CheckCircle className="h-4 w-4" />;
      case 'pending':
      case 'incomplete':
        return <Clock className="h-4 w-4" />;
      case 'failed':
      case 'canceled':
        return <XCircle className="h-4 w-4" />;
      case 'requires_action':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  // Get summary stats from API or fallback calculations
  const totalRevenue = stripeStats?.total_revenue || 0;
  const totalTransactions = stripeStats?.total_transactions || transactions.length;
  const activeSubscriptions = stripeStats?.active_subscriptions || subscriptions.filter(s => s.status === 'active').length;
  const totalCustomers = stripeStats?.total_customers || customers.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation currentPage="Payment Management" />
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Management</h1>
          <p className="text-gray-600">Monitor transactions, manage subscriptions, and analyze revenue</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(totalRevenue)}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Transactions</p>
                  <p className="text-2xl font-bold text-blue-600">{totalTransactions}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <CreditCard className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Subscriptions</p>
                  <p className="text-2xl font-bold text-purple-600">{activeSubscriptions}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Customers</p>
                  <p className="text-2xl font-bold text-orange-600">{totalCustomers}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <Users className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="transactions" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="transactions" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Transactions
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Subscriptions
            </TabsTrigger>
            <TabsTrigger value="customers" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Customers
            </TabsTrigger>
            <TabsTrigger value="sync" className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Sync
            </TabsTrigger>
          </TabsList>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Transaction History
                  </CardTitle>
                  <div className="flex items-center gap-4">
                    <Select value={dateRange} onValueChange={setDateRange}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7d">Last 7 days</SelectItem>
                        <SelectItem value="30d">Last 30 days</SelectItem>
                        <SelectItem value="90d">Last 90 days</SelectItem>
                        <SelectItem value="1y">Last year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Search and Filters */}
                <div className="flex gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by email, transaction ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="input-transaction-search"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="succeeded">Succeeded</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="requires_action">Requires Action</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {transactionsLoading ? (
                  <LoadingState message="Loading transactions..." />
                ) : filteredTransactions.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Transaction</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTransactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{transaction.id}</div>
                            <div className="text-sm text-gray-500">{transaction.description}</div>
                          </div>
                        </TableCell>
                        <TableCell>{getCustomerEmail(transaction)}</TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(transaction.amount, transaction.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(transaction.status)} className="flex items-center gap-1 w-fit">
                            {getStatusIcon(transaction.status)}
                            {transaction.status.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(transaction.created * 1000)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                ) : (
                  <div className="text-center py-12">
                    <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions found</h3>
                    <p className="text-gray-500">
                      {searchTerm || statusFilter !== 'all' 
                        ? 'Try adjusting your search criteria.' 
                        : 'No transactions have been recorded yet.'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Subscription Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Subscription</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Current Period</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptions.map((subscription) => {
                      const customer = customers.find(c => c.id === subscription.customer);
                      const firstPriceItem = subscription.items.data[0]?.price;
                      const planName = firstPriceItem?.nickname || 'Subscription';
                      const amount = firstPriceItem?.unit_amount || 0;
                      const currency = firstPriceItem?.currency || 'usd';
                      
                      return (
                        <TableRow key={subscription.id}>
                          <TableCell>
                            <div className="font-medium">{subscription.id}</div>
                          </TableCell>
                          <TableCell>{customer?.email || 'Unknown'}</TableCell>
                          <TableCell>{planName}</TableCell>
                          <TableCell>
                            <Badge variant={getStatusBadgeVariant(subscription.status)} className="flex items-center gap-1 w-fit">
                              {getStatusIcon(subscription.status)}
                              {subscription.status.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>{formatDate(subscription.current_period_start * 1000)}</div>
                              <div className="text-gray-500">to {formatDate(subscription.current_period_end * 1000)}</div>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">
                            {formatCurrency(amount, currency)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button size="sm" variant="outline">
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {subscriptionsLoading ? (
                  <LoadingState message="Loading subscriptions..." />
                ) : subscriptions.length === 0 ? (
                  <div className="text-center py-12">
                    <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No subscriptions found</h3>
                    <p className="text-gray-500">No active subscriptions to display.</p>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Customer Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Search */}
                <div className="flex gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search customers by email or name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="input-customer-search"
                    />
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Subscription Status</TableHead>
                      <TableHead>Total Spent</TableHead>
                      <TableHead>Payment Methods</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCustomers.map((customer) => (
                      <TableRow key={customer.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{customer.name || 'Unknown'}</div>
                            <div className="text-sm text-gray-500">{customer.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {customer.subscription_status ? (
                            <Badge variant={getStatusBadgeVariant(customer.subscription_status)} className="flex items-center gap-1 w-fit">
                              {getStatusIcon(customer.subscription_status)}
                              {customer.subscription_status.toUpperCase()}
                            </Badge>
                          ) : (
                            <span className="text-gray-400">No subscription</span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(customer.total_spent || 0)}
                        </TableCell>
                        <TableCell>{customer.payment_methods || 'N/A'}</TableCell>
                        <TableCell>{formatDate(customer.created)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {filteredCustomers.length === 0 && (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
                    <p className="text-gray-500">
                      {searchTerm ? 'Try adjusting your search criteria.' : 'No customers to display.'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sync Tab */}
          <TabsContent value="sync">
            <div className="space-y-6">
              {/* Sync Status Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    Stripe Synchronization Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {syncStatusLoading ? (
                    <LoadingState message="Loading sync status..." />
                  ) : syncStatus ? (
                    <div className="space-y-6">
                      {/* Status Overview */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-blue-600">Total Users</p>
                              <p className="text-2xl font-bold text-blue-900">{syncStatus.total_users}</p>
                            </div>
                            <Users className="h-8 w-8 text-blue-500" />
                          </div>
                        </div>

                        <div className="bg-green-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-green-600">Synced with Stripe</p>
                              <p className="text-2xl font-bold text-green-900">{syncStatus.users_with_stripe}</p>
                            </div>
                            <CheckCircle className="h-8 w-8 text-green-500" />
                          </div>
                        </div>

                        <div className="bg-red-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-red-600">Missing Stripe IDs</p>
                              <p className="text-2xl font-bold text-red-900">{syncStatus.users_without_stripe}</p>
                            </div>
                            <AlertCircle className="h-8 w-8 text-red-500" />
                          </div>
                        </div>

                        <div className="bg-purple-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-purple-600">Lifetime Users</p>
                              <p className="text-2xl font-bold text-purple-900">{syncStatus.lifetime_users}</p>
                            </div>
                            <TrendingUp className="h-8 w-8 text-purple-500" />
                          </div>
                        </div>
                      </div>

                      {/* Sync Progress */}
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Sync Progress</span>
                          <span className="text-sm text-gray-600">{syncStatus.sync_percentage.toFixed(1)}%</span>
                        </div>
                        <Progress value={syncStatus.sync_percentage} className="h-3" />
                      </div>

                      {/* Status Alerts */}
                      {syncStatus.users_without_stripe > 0 && (
                        <Alert>
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            <strong>Sync Issue Detected:</strong> {syncStatus.users_without_stripe} users are not synced with Stripe customers. 
                            This can cause payment and subscription management issues.
                          </AlertDescription>
                        </Alert>
                      )}

                      {!syncStatus.lifetime_product_exists && (
                        <Alert>
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            <strong>Missing Lifetime Product:</strong> No $0 lifetime product found in Stripe. 
                            This is required for Neural Quantum lifetime users.
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Unable to load sync status</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sync Actions Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <RefreshCw className="h-5 w-5" />
                    Synchronization Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex flex-col md:flex-row gap-4">
                      <Button
                        onClick={() => refetchSyncStatus()}
                        disabled={syncStatusLoading}
                        variant="outline"
                        className="flex items-center gap-2"
                        data-testid="button-refresh-sync-status"
                      >
                        {syncStatusLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        Refresh Status
                      </Button>

                      <Button
                        onClick={() => syncCustomersMutation.mutate()}
                        disabled={syncCustomersMutation.isPending}
                        className="flex items-center gap-2"
                        data-testid="button-sync-customers"
                      >
                        {syncCustomersMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Users className="h-4 w-4" />
                        )}
                        {syncCustomersMutation.isPending ? 'Syncing...' : 'Sync All Customers'}
                      </Button>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium mb-2">What does "Sync All Customers" do?</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Creates Stripe customer records for users without them</li>
                        <li>• Sets up $0 lifetime products for Neural Quantum users</li>
                        <li>• Updates user metadata and subscription status</li>
                        <li>• Ensures payment system consistency</li>
                      </ul>
                    </div>

                    {syncCustomersMutation.isSuccess && syncCustomersMutation.data && (
                      <Alert>
                        <CheckCircle className="h-4 w-4" />
                        <AlertDescription>
                          <strong>Sync Completed Successfully!</strong>
                          {syncCustomersMutation.data.summary && (
                            <div className="mt-2 text-sm">
                              • Processed: {syncCustomersMutation.data.summary.total_users_processed} users<br/>
                              • Success: {syncCustomersMutation.data.summary.success_count}<br/>
                              • Failed: {syncCustomersMutation.data.summary.failed_count}<br/>
                              • Lifetime: {syncCustomersMutation.data.summary.lifetime_count}<br/>
                              • New sync rate: {syncCustomersMutation.data.summary.sync_percentage_after?.toFixed(1)}%
                            </div>
                          )}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}